// Digest de pendências por usuário (e-mail). Puro — sem React/Supabase — para
// ser reutilizado tanto no app quanto no Worker de alertas agendado.
import { computeProductSummary } from './criticalPath';
import { buildTemplateForType } from './terceirizacao';
import type { AppRoleLike, Product, StageTemplate } from './types';

export interface PendingItem {
  productId: string;
  brand: string;
  productName: string;
  n: number;
  phase: string;
  stageName: string;
  areaKeys: string[];
  kind: 'em_andamento' | 'pronta';
  atrasoDias: number; // >0 = vencida/parada há N dias
  previsaoFim: string | null; // 'YYYY-MM-DD'
}

export interface Recipient {
  name: string;
  email: string;
  role: AppRoleLike; // 'admin' | 'gestor' | 'editor' | 'viewer'
  areaKeys: string[];
}

const isPMO = (r: Recipient) => r.role === 'admin' || r.role === 'gestor';

/** Pendências (etapas em andamento ou prontas) relevantes ao destinatário. */
export function pendingFor(
  recipient: Recipient,
  products: Product[],
  template: StageTemplate[],
  today: Date = new Date(),
): PendingItem[] {
  if (recipient.role === 'viewer') return [];
  const items: PendingItem[] = [];
  for (const p of products) {
    // Cancelado, concluído e "em espera" (pausado) não geram alertas.
    // Ao voltar para "ativo", os alertas são retomados automaticamente.
    if (p.lifecycle === 'cancelado' || p.lifecycle === 'concluido' || p.lifecycle === 'pausado')
      continue;
    const tpl = buildTemplateForType(template, p.projectType ?? 'interno');
    const summary = computeProductSummary(p, tpl, today);
    const statusByN = new Map(p.stages.map((s) => [s.n, s.status]));
    const compByN = new Map(summary.stages.map((c) => [c.n, c]));
    for (const t of tpl) {
      const st = statusByN.get(t.n) ?? 'nao_iniciado';
      const ready =
        st === 'nao_iniciado' &&
        t.deps.every((d) => (statusByN.get(d) ?? 'nao_iniciado') === 'concluido');
      if (st !== 'em_andamento' && !ready) continue;

      const relevant = isPMO(recipient) || t.areaKeys.some((a) => recipient.areaKeys.includes(a));
      if (!relevant) continue;

      const comp = compByN.get(t.n);
      items.push({
        productId: p.id,
        brand: p.brand,
        productName: p.name,
        n: t.n,
        phase: t.phase,
        stageName: t.name,
        areaKeys: t.areaKeys,
        kind: st === 'em_andamento' ? 'em_andamento' : 'pronta',
        atrasoDias: comp?.atrasoDias ?? 0,
        previsaoFim: comp?.predictedFinish ?? null,
      });
    }
  }
  // Mais atrasadas primeiro; depois por projeto/etapa.
  return items.sort(
    (a, b) =>
      b.atrasoDias - a.atrasoDias ||
      a.productName.localeCompare(b.productName) ||
      a.n - b.n,
  );
}

function fmtBR(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function prazoTexto(it: PendingItem): { label: string; tone: 'red' | 'amber' | 'green' } {
  if (it.atrasoDias > 0) return { label: `vencida há ${it.atrasoDias} d`, tone: 'red' };
  const prev = it.previsaoFim ? `prevista p/ ${fmtBR(it.previsaoFim)}` : 'sem previsão';
  return { label: prev, tone: 'green' };
}

export interface EmailOut {
  subject: string;
  html: string;
  text: string;
}

/** Monta o e-mail (HTML com estilo inline + texto puro) do digest de pendências. */
export function buildPendenciasEmail(
  recipient: Recipient,
  items: PendingItem[],
  today: Date = new Date(),
  opts: { appUrl?: string } = {},
): EmailOut {
  const appUrl = opts.appUrl ?? 'https://npd-trevo.pages.dev';
  const dataHoje = today.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  const vencidas = items.filter((i) => i.atrasoDias > 0).length;
  const emAndamento = items.filter((i) => i.kind === 'em_andamento').length;
  const prontas = items.filter((i) => i.kind === 'pronta').length;
  const escopo = isPMO(recipient) ? 'de todos os projetos' : 'das suas áreas';

  const subject =
    items.length === 0
      ? `NPD Trevo · sem pendências ${escopo} hoje 🎉`
      : `NPD Trevo · ${items.length} tarefa(s) pendente(s) ${escopo}${vencidas ? ` · ${vencidas} vencida(s)` : ''}`;

  // ---- agrupa por projeto ----
  const byProj = new Map<string, PendingItem[]>();
  for (const it of items) {
    const key = `${it.brand} · ${it.productName}||${it.productId}`;
    (byProj.get(key) ?? byProj.set(key, []).get(key)!).push(it);
  }

  const NAVY = '#1f3a73', BLUE = '#1e56a0', INK = '#1b2333', MUT = '#5a6b85', LINE = '#e4eaf6';
  const toneColor = { red: '#c41018', amber: '#8a6100', green: '#16786b' };

  const rowsHtml = [...byProj.entries()]
    .map(([key, its]) => {
      const parts = key.split('||');
      const title = parts[0] ?? '';
      const pid = parts[1] ?? '';
      const lis = its
        .map((it) => {
          const p = prazoTexto(it);
          const tag =
            it.kind === 'em_andamento'
              ? `<span style="font-size:11px;color:${BLUE};font-weight:700">em andamento</span>`
              : `<span style="font-size:11px;color:${toneColor.green};font-weight:700">pronta p/ iniciar</span>`;
          return `<tr>
            <td style="padding:7px 10px;border-bottom:1px solid ${LINE};font-size:13px;color:${INK}">
              <b>${it.phase}</b> · ${it.stageName} &nbsp; ${tag}
            </td>
            <td style="padding:7px 10px;border-bottom:1px solid ${LINE};font-size:12.5px;font-weight:700;text-align:right;color:${toneColor[p.tone]};white-space:nowrap">
              ${p.label}
            </td>
          </tr>`;
        })
        .join('');
      return `<div style="margin:0 0 16px">
        <div style="font-size:14px;font-weight:700;color:${NAVY};margin:0 0 6px">
          <a href="${appUrl}/produto/${pid}" style="color:${NAVY};text-decoration:none">${title} ›</a>
        </div>
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;border:1px solid ${LINE};border-radius:8px;overflow:hidden">${lis}</table>
      </div>`;
    })
    .join('');

  const chips = `
    <table role="presentation" cellspacing="0" cellpadding="0" style="margin:10px 0 18px"><tr>
      ${chip('Vencidas', vencidas, '#c41018')}
      ${chip('Em andamento', emAndamento, BLUE)}
      ${chip('Prontas p/ iniciar', prontas, '#16786b')}
    </tr></table>`;

  const corpo =
    items.length === 0
      ? `<p style="font-size:14px;color:${INK}">Você está em dia — nenhuma tarefa pendente ${escopo} hoje. 👏</p>`
      : `${chips}${rowsHtml}`;

  const html = `<!doctype html><html><body style="margin:0;background:#eef2f8;padding:24px;font-family:Arial,Helvetica,sans-serif">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td align="center">
    <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width:600px;background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 6px 24px rgba(20,40,80,.10)">
      <tr><td style="background:${NAVY};padding:20px 24px">
        <div style="color:#fff;font-size:18px;font-weight:700;letter-spacing:.2px">Workflow de Novos Produtos · Trevo Lácteos</div>
        <div style="color:#c9d6ef;font-size:12.5px;margin-top:2px">Resumo de pendências — ${dataHoje}</div>
      </td></tr>
      <tr><td style="padding:22px 24px">
        <p style="font-size:14px;color:${INK};margin:0 0 4px">Olá, <b>${recipient.name}</b>.</p>
        <p style="font-size:13px;color:${MUT};margin:0 0 8px">
          Aqui está o resumo das tarefas pendentes ${escopo}.
        </p>
        ${corpo}
        <div style="margin:22px 0 6px">
          <a href="${appUrl}" style="background:${BLUE};color:#fff;text-decoration:none;font-size:13px;font-weight:700;padding:11px 20px;border-radius:8px;display:inline-block">Abrir o painel</a>
        </div>
        <p style="font-size:11px;color:${MUT};margin:18px 0 0">
          Você recebe este e-mail porque participa do processo de novos produtos da Trevo Lácteos.
        </p>
      </td></tr>
    </table>
  </td></tr></table>
  </body></html>`;

  const text = [
    `Workflow de Novos Produtos · Trevo Lácteos — pendências (${dataHoje})`,
    `Olá, ${recipient.name}.`,
    items.length === 0
      ? `Sem pendências ${escopo} hoje.`
      : `${items.length} tarefa(s) pendente(s) ${escopo} (${vencidas} vencida(s)).`,
    '',
    ...items.map((it) => {
      const p = prazoTexto(it);
      return `- [${it.brand} · ${it.productName}] ${it.phase} ${it.stageName} — ${p.label}`;
    }),
    '',
    `Abrir o painel: ${appUrl}`,
  ].join('\n');

  return { subject, html, text };
}

function chip(label: string, n: number, color: string): string {
  return `<td style="padding:0 8px 0 0">
    <table role="presentation" cellspacing="0" cellpadding="0"><tr>
      <td style="background:${color}1a;border:1px solid ${color}55;border-radius:8px;padding:6px 12px;text-align:center">
        <div style="font-size:19px;font-weight:800;color:${color};line-height:1">${n}</div>
        <div style="font-size:10px;color:${color};text-transform:uppercase;letter-spacing:.06em;margin-top:2px">${label}</div>
      </td>
    </tr></table>
  </td>`;
}
